import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, LoaderIcon } from 'lucide-react';
import { useStore } from '../contexts/StoreContext';
import { Field } from './ui/Field';
import { LeadWhatsAppHandoff } from './LeadWhatsAppHandoff';
import { Vehicle } from '../types/catalog';
import { ContactPref, Lead, LeadChannel } from '../types/lead';
import { OfferContext, SimulationResult } from '../utils/finance';
import { cleanText, isValidEmail, isValidName, isValidPhone, normalizePhone } from '../utils/validation';
import { vehicleLabel } from '../utils/catalog';
import { formatARS } from '../utils/format';
interface InquiryFormProps {
  channel: LeadChannel;
  vehicle?: Vehicle | null;
  simulation?: {
    sim: SimulationResult;
    ctx: OfferContext;
  } | null;
  allowQuote?: boolean;
  defaultMessage?: string;
  idPrefix?: string;
}
interface Errors {
  name?: string;
  phone?: string;
  email?: string;
  consent?: string;
}
export function InquiryForm({
  channel,
  vehicle,
  simulation,
  allowQuote,
  defaultMessage = '',
  idPrefix = 'inq'
}: InquiryFormProps) {
  const {
    addLead,
    brands
  } = useStore();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [pref, setPref] = useState<ContactPref>('whatsapp');
  const [message, setMessage] = useState(defaultMessage);
  const [wantsQuote, setWantsQuote] = useState(false);
  const [consent, setConsent] = useState(false);
  const [contactConsent, setContactConsent] = useState(true);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState<Lead | null>(null);
  const validate = (): Errors => {
    const e: Errors = {};
    if (!isValidName(name)) e.name = 'Ingresá tu nombre (sólo letras, mínimo 2 caracteres).';
    if (!isValidPhone(phone)) e.phone = 'Ingresá un teléfono válido con código de área.';
    if ((pref === 'email' || email) && !isValidEmail(email)) e.email = 'Ingresá un email válido.';
    if (!consent) e.consent = 'Necesitamos tu consentimiento para registrar la consulta.';
    return e;
  };
  const onSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    const sim = simulation?.sim;
    const created = addLead({
      channel: wantsQuote ? 'cotizacion' : channel,
      name: cleanText(name, 80),
      phone: normalizePhone(phone),
      email: cleanText(email, 120),
      contactPref: pref,
      vehicleId: vehicle?.id ?? null,
      vehicleLabel: vehicle ? vehicleLabel(vehicle, brands) : null,
      price: vehicle?.price ?? null,
      down: sim?.down ?? null,
      term: sim?.term ?? null,
      simulation: sim && simulation ? {
        tna: sim.tna,
        cft: simulation.ctx.offer.cft,
        amortization: simulation.ctx.offer.amortization,
        term: sim.term,
        installment: sim.firstInstallment,
        lastInstallment: sim.lastInstallment,
        totalCost: sim.totalCost,
        verified: simulation.ctx.offer.verified,
        offerSource: simulation.ctx.source
      } : null,
      message: cleanText(message, 800),
      credit: null,
      consents: {
        dataProcessing: consent,
        contact: contactConsent,
        at: new Date().toISOString()
      }
    });
    setLead(created);
    setSubmitting(false);
  };
  if (lead) {
    return <div className="rounded-2xl border border-ok/30 bg-ok/5 p-5" role="status" data-id="element-872" data-mp="3265ttbwy76x7">
        <CheckCircle2Icon className="h-6 w-6 text-ok" aria-hidden data-id="element-873" data-mp="2xd4dr0l091c1" />
        <h3 className="mt-3 font-display text-lg font-bold" data-id="element-874" data-mp="2tdlqhy5poe61">Consulta registrada</h3>
        <p className="mt-1 text-sm text-mist" data-id="element-875" data-mp="3puq7f0yohh98">
          Tu número de seguimiento es <span className="font-mono font-semibold text-white" data-id="element-876" data-mp="3pwt6eaht6kq3">{lead.code}</span>. Un asesor comercial revisará tu consulta y te
          contactará por {lead.contactPref === 'whatsapp' ? 'WhatsApp' : lead.contactPref === 'llamada' ? 'teléfono' : 'email'}.
        </p>
        <LeadWhatsAppHandoff lead={lead} data-id="element-877" data-mp="3amwi6un6dkvr" />
        <Link to={`/chequeo?code=${lead.code}#seguimiento`} className="btn btn-ghost mt-4 w-full" data-id="element-878" data-mp="2wlk3emjr3pbg">
          Ver estado de mi consulta
        </Link>
      </div>;
  }
  return <form onSubmit={onSubmit} noValidate className="space-y-4" data-id="element-879" data-mp="30p8c58k4cqya">
      {simulation && <div className="rounded-xl border border-line bg-panel p-3 text-xs text-mist" data-id="element-880" data-mp="3odi0ah4t1qsl">
          Se adjunta tu simulación: anticipo {formatARS(simulation.sim.down)}, {simulation.sim.term} cuotas de {formatARS(simulation.sim.firstInstallment)}
          {simulation.ctx.offer.amortization === 'aleman' ? ' (primera cuota)' : ''}.
        </div>}
      <Field label="Nombre y apellido" htmlFor={`${idPrefix}-name`} error={errors.name} data-id="element-881" data-mp="b5ojg8qhrkef">
        <input id={`${idPrefix}-name`} className="input" value={name} onChange={e => setName(e.target.value)} autoComplete="name" maxLength={80} data-id="element-882" data-mp="fxg8ufxwvk2z" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-883" data-mp="20l8wpuabmaaa">
        <Field label="Teléfono / WhatsApp" htmlFor={`${idPrefix}-phone`} error={errors.phone} data-id="element-884" data-mp="1vp0koprf6x7c">
          <input id={`${idPrefix}-phone`} className="input" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" placeholder="11 5555 5555" maxLength={20} data-id="element-885" data-mp="2adelyw96udls" />
        </Field>
        <Field label={pref === 'email' ? 'Email' : 'Email (opcional)'} htmlFor={`${idPrefix}-email`} error={errors.email} data-id="element-886" data-mp="1hpir7x36ivrn">
          <input id={`${idPrefix}-email`} type="email" className="input" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" maxLength={120} data-id="element-887" data-mp="1cqxuw8r1sw8h" />
        </Field>
      </div>
      <fieldset data-id="element-888" data-mp="1mlzi2z1rjo8f">
        <legend className="label" data-id="element-889" data-mp="1mpncs1iy2goq">¿Cómo preferís que te contactemos?</legend>
        <div className="grid grid-cols-3 gap-2" data-id="element-890" data-mp="330943mij90w3">
          {(['whatsapp', 'llamada', 'email'] as ContactPref[]).map(p => <label key={p} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-center text-sm font-medium transition-colors duration-150 ${pref === p ? 'border-neon bg-neon/10 text-white' : 'border-line text-mist hover:text-white'}`} data-id="element-891" data-mp="2y526mwfuwsfm">
              <input type="radio" name={`${idPrefix}-pref`} value={p} checked={pref === p} onChange={() => setPref(p)} className="sr-only" data-id="element-892" data-mp="3f3tujtbamr2h" />
              {p === 'whatsapp' ? 'WhatsApp' : p === 'llamada' ? 'Llamada' : 'Email'}
            </label>)}
        </div>
      </fieldset>
      <Field label="Mensaje (opcional)" htmlFor={`${idPrefix}-msg`} data-id="element-893" data-mp="2rdzmrq5tzsp3">
        <textarea id={`${idPrefix}-msg`} className="textarea" rows={3} value={message} onChange={e => setMessage(e.target.value)} maxLength={800} data-id="element-894" data-mp="2rc6bfxk9wkar" />
      </Field>
      {allowQuote && <label className="flex items-start gap-3 text-sm text-mist" data-id="element-895" data-mp="316f4qnh4oh9z">
          <input type="checkbox" checked={wantsQuote} onChange={e => setWantsQuote(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1FA8FF]" data-id="element-896" data-mp="23o5apatc685w" />
          Quiero recibir una cotización formal de esta unidad.
        </label>}
      <div className="space-y-2 rounded-xl border border-line p-3" data-id="element-897" data-mp="pqkijd0q49l9">
        <label className="flex items-start gap-3 text-xs text-mist" data-id="element-898" data-mp="lrtiax0yobtx">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1FA8FF]" aria-invalid={!!errors.consent} data-id="element-899" data-mp="grfi215lq66g" />
          <span data-id="element-900" data-mp="6tspwczaki4j">
            Acepto que VCARS trate mis datos personales sólo para responder esta consulta, conforme a la Ley 25.326 de Protección de Datos Personales. Puedo
            solicitar su acceso, rectificación o supresión en cualquier momento.
          </span>
        </label>
        <label className="flex items-start gap-3 text-xs text-mist" data-id="element-901" data-mp="3u4r37u6x3app">
          <input type="checkbox" checked={contactConsent} onChange={e => setContactConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1FA8FF]" data-id="element-902" data-mp="3p9v7aiyk837b" />
          <span data-id="element-903" data-mp="3f9cid9s51wij">Acepto que un asesor comercial me contacte por el medio elegido.</span>
        </label>
        {errors.consent && <p className="text-xs text-danger" role="alert" data-id="element-904" data-mp="17zyync32q1bo">{errors.consent}</p>}
      </div>
      <button type="submit" className="btn btn-primary w-full" disabled={submitting} data-id="element-905" data-mp="1bz7s0fu7bx6p">
        {submitting && <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden data-id="element-906" data-mp="xzetvzgcieop" />}
        Enviar consulta
      </button>
    </form>;
}